import { createContext, useContext, useMemo } from 'react'
import { useStoredState } from '../hooks/useStoredState'
import { FONT_SIZE_DEFAULT, FONT_SIZE_KEY } from '../utils/constants/localStorage.constants'

interface FontSizeContextType {
  fontSize: number
  increase: () => void
  decrease: () => void
  reset: () => void
}

export const FontSizeContext = createContext<FontSizeContextType>({
  fontSize: FONT_SIZE_DEFAULT,
  increase: () => {},
  decrease: () => {},
  reset: () => {},
})

interface FontSizeProviderProps {
  children: React.ReactNode
}

export const FontSizeProvider: React.FC<FontSizeProviderProps> = ({ children }) => {
  const [fontSize, setFontSize] = useStoredState({
    key: FONT_SIZE_KEY,
    defaultValue: FONT_SIZE_DEFAULT,
  })

  const increase = () => setFontSize((size: number) => Math.min(size + 1, 30))
  const decrease = () => setFontSize((size: number) => Math.max(size - 1, 10))
  const reset = () => setFontSize(FONT_SIZE_DEFAULT)

  // Apply and save to localStorage with each change
  document.documentElement.style.fontSize = `${fontSize}px`

  const value = useMemo(
    () => ({ fontSize, increase, decrease, reset }),
    [fontSize, increase, decrease, reset]
  )

  return <FontSizeContext.Provider value={value}>{children}</FontSizeContext.Provider>
}

export const useFontSize = () => useContext(FontSizeContext)
